import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';
import type { Advice } from 'types/Advice';
import type { IdsState } from 'modules/works/reducers/data/ids';
import type { GroupIdsState } from 'modules/works/reducers/data/groupIds';

import { getEntity } from 'modules/entity/selectors';
import { ADVICE_STATUS, CHECKLIST_TYPE, SEVERITY } from 'modules/entity/constants';
import { getAdvices as getInterventionAdvicesUtils, isAdviceAccepted } from 'works/utils';

import { getData, getSelectedChecklistGroupId } from '..';

const getProps = <T, >(_: RootState, props: T) => props;

const getAdvicesData = createSelector(getData, data => data.advices);
export const getIds = createSelector(getData, data => data.ids);
const getGroupIds = createSelector(getData, data => data.groupIds);
const getCollection = createSelector(getAdvicesData, advices => advices.byId);

export const getAdvices = createSelector(getCollection, (collection): Array<Advice> => Object.values(collection));
export const getPendingAdvices = createSelector(getAdvices, advices =>
  advices.filter(
    ({ status, severity, checklistType }) =>
      status === ADVICE_STATUS.PENDING &&
      checklistType !== CHECKLIST_TYPE.DONE &&
      severity !== SEVERITY.NONE &&
      severity !== SEVERITY.UNDEFINED
  ));
export const hasPendingAdvices = createSelector(getPendingAdvices, advices => advices.length > 0);
export const getAdvicesByIds = createSelector(getAdvices, getProps, (collection, ids: Array<string>): Array<Advice> =>
  ids
    .map(id => collection.find(({ adviceId }) => adviceId === id))
    .filter((advice): advice is Advice => advice !== undefined));

const getInterventionAdvices = createSelector(getEntity, getInterventionAdvicesUtils);
export const getInterventionAdvicesIds = createSelector(getInterventionAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

export const getAdvice = createSelector(getCollection, getProps, (advices, id) => advices[id]);

export const getOfferedAdvices = createSelector(getAdvices, advices =>
  advices.filter(
    ({ checklistType, severity }) =>
      (checklistType === CHECKLIST_TYPE.WORK || checklistType === CHECKLIST_TYPE.REMINDER_SCHEDULED) &&
      severity === SEVERITY.OFFERED
  ));

export const getOfferedAdvicesIds = createSelector(getOfferedAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

export const getAcceptedAdvices = createSelector(getAdvices, advices => advices.filter(isAdviceAccepted));

export const getCheckableAdviceIds = createSelector(getAdvicesByIds, advices =>
  advices.reduce<Array<string>>((acc, advice) => [...acc, ...(advice?.status === ADVICE_STATUS.PENDING ? [advice.adviceId] : [])], []));

export const hasOnlyDiscountPending = createSelector(getPendingAdvices, advices =>
  advices.every(advice => advice.checklistType === CHECKLIST_TYPE.DISCOUNT));

const getChecklistGroupId = createSelector(
  getSelectedChecklistGroupId,
  getProps,
  (selectedGroupId, givenGroupId) => givenGroupId ?? selectedGroupId
);

export const getAdviceIdsByType = createSelector(
  getIds,
  getGroupIds,
  getChecklistGroupId,
  (idsByType: IdsState, idsByGroupAndType: GroupIdsState, checklistGroupId) =>
    (checklistGroupId ? idsByGroupAndType[checklistGroupId] : idsByType) ?? {}
);
